import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { ProductDetail, ProductSummary } from '@/types'

import Head from 'next/head'
import Link from 'next/link'
import { getProductBySlug, getRelatedProducts } from '@/lib/catalog'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Container from '@/components/generals/container'
import RichText from '@/components/generals/rich-text'
import Price from '@/components/shop/price'
import ProductCard from '@/components/shop/product-card'
import ProductImage from '@/components/shop/product-image'
import ProductPurchase from '@/components/shop/product-purchase'
import { siteConfig, storeInfo } from '@/constants/site'

import {
    ChevronRight,
    Disc3,
    Phone,
    Store
} from 'lucide-react'

interface ProductPageProps {
    product: ProductDetail
    related: Array<ProductSummary>
}

export const getServerSideProps: GetServerSideProps<ProductPageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true }

    const product = await getProductBySlug(slug)

    if (!product) return { notFound: true }

    const related = await getRelatedProducts(product, 4).catch(() => [])

    return { props: { product, related } }
}

const ProductPage: FC<ProductPageProps> = ({ product, related }): ReactNode => {
    const jsonLd = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.tagline ?? undefined,
        image: product.image ?? undefined,
        sku: product.slug,
        category: product.category_name ?? undefined,
        offers: {
            '@type': 'Offer',
            priceCurrency: storeInfo.currency,
            price: product.price.toFixed(2),
            availability: product.in_stock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
        }
    }).replace(/</g, '\\u003c')

    return (
        <Layout>
            <Seo
                title={product.name}
                description={product.tagline ?? siteConfig.description}
                ogImage={product.image ?? undefined}
            />

            <Head>
                <script
                    key='product-jsonld'
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: jsonLd }}
                />
            </Head>

            <Container className='py-8 sm:py-12'>
                <nav aria-label='Breadcrumb'>
                    <ol className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                        <li>
                            <Link href='/shop' className='font-medium hover:text-foreground'>
                                Shop
                            </Link>
                        </li>

                        {product.category_id && product.category_slug && product.category_name && (
                            <>
                                <li aria-hidden='true'>
                                    <ChevronRight className='size-3.5' />
                                </li>

                                <li>
                                    <Link
                                        href={{ pathname: '/shop', query: { category: product.category_slug } }}
                                        className='font-medium hover:text-foreground'
                                    >
                                        <span data-mdlfy-cms={`categories:${product.category_id}:name`}>
                                            {product.category_name}
                                        </span>
                                    </Link>
                                </li>
                            </>
                        )}

                        <li aria-hidden='true'>
                            <ChevronRight className='size-3.5' />
                        </li>

                        <li aria-current='page' className='min-w-0 truncate font-medium text-foreground'>
                            <span data-mdlfy-cms={`products:${product.id}:name`}>
                                {product.name}
                            </span>
                        </li>
                    </ol>
                </nav>

                <div className='mt-6 grid gap-10 lg:grid-cols-2 lg:gap-16'>
                    <div className='lg:sticky lg:top-32 lg:self-start'>
                        <div className='relative aspect-square overflow-hidden rounded-[2rem] ring-1 ring-border'>
                            <ProductImage
                                src={product.image}
                                alt={product.image_alt ?? product.name}
                                categorySlug={product.category_slug}
                                iconClassName='size-24'
                                loading='eager'
                            />
                        </div>
                    </div>

                    <div className='lg:py-4'>
                        {product.category_id && product.category_name && (
                            <p className='text-xs font-bold tracking-[0.18em] text-tide uppercase'>
                                <span data-mdlfy-cms={`categories:${product.category_id}:name`}>
                                    {product.category_name}
                                </span>
                            </p>
                        )}

                        <h1 className='mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl'>
                            <span data-mdlfy-cms={`products:${product.id}:name`}>
                                {product.name}
                            </span>
                        </h1>

                        {product.tagline && (
                            <p
                                data-mdlfy-cms={`products:${product.id}:tagline`}
                                className='mt-4 text-lg leading-relaxed text-muted-foreground'
                            >
                                {product.tagline}
                            </p>
                        )}

                        <div className='mt-6 flex flex-wrap items-center gap-x-5 gap-y-2'>
                            <Price price={product.price} compareAt={product.compare_at_price} size='lg' />

                            {product.in_stock
                                ? (
                                    <p className='flex items-center gap-2 text-sm font-semibold text-tide'>
                                        <span className='size-2 rounded-full bg-tide' aria-hidden='true' />
                                        In stock, ready to reserve
                                    </p>
                                )
                                : (
                                    <p className='flex items-center gap-2 text-sm font-semibold text-muted-foreground'>
                                        <span className='size-2 rounded-full bg-muted-foreground' aria-hidden='true' />
                                        Sold out right now
                                    </p>
                                )}
                        </div>

                        <ProductPurchase key={product.id} product={product} />

                        <ul className='mt-10 grid gap-3 text-sm sm:grid-cols-2'>
                            <li className='flex gap-3 rounded-2xl bg-card p-4 ring-1 ring-border'>
                                <Store className='mt-0.5 size-5 shrink-0 text-tide' />

                                <span>
                                    <span className='block font-semibold'>
                                        Reserve now, pay in store
                                    </span>

                                    <span className='text-muted-foreground'>
                                        We set it aside for the day you choose.
                                    </span>
                                </span>
                            </li>

                            <li className='flex gap-3 rounded-2xl bg-card p-4 ring-1 ring-border'>
                                <Disc3 className='mt-0.5 size-5 shrink-0 text-tide' />

                                <span>
                                    <span className='block font-semibold'>
                                        Free line spooling
                                    </span>

                                    <span className='text-muted-foreground'>
                                        On any line you buy here, while you wait.
                                    </span>
                                </span>
                            </li>

                            <li className='flex gap-3 rounded-2xl bg-card p-4 ring-1 ring-border sm:col-span-2'>
                                <Phone className='mt-0.5 size-5 shrink-0 text-tide' />

                                <span>
                                    <span className='block font-semibold'>
                                        Not sure it is the right fit?
                                    </span>

                                    <span className='text-muted-foreground'>
                                        Call us on{' '}

                                        <a href={storeInfo.phoneHref} className='font-semibold text-foreground underline underline-offset-4'>
                                            {storeInfo.phone}
                                        </a>

                                        {' '}and tell us where you are fishing.
                                    </span>
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>

                {(product.description || product.specs.length > 0) && (
                    <section aria-label='Product details' className='mt-16 grid gap-10 border-t border-border pt-12 lg:grid-cols-12 lg:gap-16'>
                        {product.description && (
                            <div className='lg:col-span-7'>
                                <h2 className='text-2xl font-extrabold tracking-tight'>
                                    About this gear
                                </h2>

                                <RichText
                                    html={product.description}
                                    className='mt-5 text-base leading-relaxed text-foreground/85'
                                />
                            </div>
                        )}

                        {product.specs.length > 0 && (
                            <div className={product.description ? 'lg:col-span-5' : 'lg:col-span-7'}>
                                <h2 className='text-2xl font-extrabold tracking-tight'>
                                    Specs
                                </h2>

                                <dl className='mt-5 divide-y divide-border overflow-hidden rounded-3xl bg-card ring-1 ring-border'>
                                    {product.specs.map(spec => (
                                        <div key={spec.label} className='grid grid-cols-5 gap-4 px-5 py-3.5 text-sm'>
                                            <dt className='col-span-2 text-muted-foreground'>
                                                {spec.label}
                                            </dt>

                                            <dd className='col-span-3 font-semibold'>
                                                {spec.value}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}
                    </section>
                )}

                {related.length > 0 && (
                    <section aria-labelledby='related-title' className='mt-20'>
                        <div className='flex flex-wrap items-end justify-between gap-4'>
                            <h2 id='related-title' className='text-3xl font-extrabold tracking-tight'>
                                Pairs well with
                            </h2>

                            <Link href='/shop' className='text-sm font-semibold underline underline-offset-4'>
                                See all gear
                            </Link>
                        </div>

                        <div className='mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                            {related.map(item => (
                                <ProductCard key={item.id} product={item} />
                            ))}
                        </div>
                    </section>
                )}
            </Container>
        </Layout>
    )
}

export default ProductPage
